import { isUndefined } from 'lodash-es';
import { type KeyboardEventHandler, useRef, useState } from 'react';

import type { RelativeAbsolutePosition } from '../../types/pointer-events.js';
import { SELECTION_RIGHT_OVERFLOW } from '../interactions-overlay/constants.js';
import { normalizeSelectionArea } from '../interactions-overlay/utils/normalize-selection-area.js';
import { KNOB_HEIGHT } from '../overlays/constants.js';
import { SelectionEdge } from './components/SelectionEdge.js';
import { SelectionBackdrop } from './SelectionBackdrop.js';
import { changedEdge, LEFT_EDGE_ID, RIGHT_EDGE_ID } from './utils.js';
import { clampSelectionArea } from './utils/clamp-selection-area.js';

interface SelectionProps {
  bounds: DOMRect;
  focusable: boolean;
  interactable: boolean;
  startX: number;
  endX: number;
  height: number;
  width: number;
  handleSelectionAreaStartKeyDown: KeyboardEventHandler;
  handleSelectionAreaEndKeyDown: KeyboardEventHandler;
  start: RelativeAbsolutePosition;
  end: RelativeAbsolutePosition;
  onSelectionChange: ({
    selection,
    accessible,
  }: {
    selection: [RelativeAbsolutePosition, RelativeAbsolutePosition];
    accessible: boolean;
  }) => void;
  bottomFormatter?: (value: number) => string;
  topFormatter?: (value: number) => string;
  isBottomLabelRotated?: boolean;
  isTopLabelRotated?: boolean;
}

const extendX = (relativeAbsolute: RelativeAbsolutePosition, x: number) => ({
  ...relativeAbsolute,
  absolute: {
    ...relativeAbsolute.absolute,
    x: relativeAbsolute.absolute.x + x,
  },
  relative: {
    ...relativeAbsolute.relative,
    x: relativeAbsolute.relative.x + x,
  },
});

export const Selection = (props: SelectionProps) => {
  const {
    startX,
    endX,
    height,
    width,
    focusable,
    interactable,
    bottomFormatter,
    topFormatter,
    bounds,
    handleSelectionAreaStartKeyDown,
    handleSelectionAreaEndKeyDown,
    start,
    end,
    onSelectionChange,
    isBottomLabelRotated = false,
    isTopLabelRotated = false,
  } = props;
  const range: [number, number] = [0, width + SELECTION_RIGHT_OVERFLOW];
  /**
   * Stores the selection edge that is NOT being adjusted during selection adjustment and acts as pivot
   */
  const selectionEdgePivotRef = useRef<RelativeAbsolutePosition>(undefined);
  const [hoveredEdge, setHoveredEdge] = useState('');
  const onDragMove = (x: number) => {
    // Adjust selection edges if dragging from one of the edges
    if (!isUndefined(selectionEdgePivotRef.current)) {
      const position = {
        relative: {
          x: x - bounds.left,
          y: height / 2,
        },
        absolute: {
          y: height / 2 + bounds.top,
          x: x,
        },
      };

      const nextSelection = clampSelectionArea(
        normalizeSelectionArea([position, selectionEdgePivotRef.current]),
        range,
      );
      const edge = changedEdge([startX, endX], nextSelection);
      if (edge) {
        setHoveredEdge(edge);
      }

      onSelectionChange?.({ selection: nextSelection, accessible: false });
    }
  };

  const onWholeSelectionMove = (x: number) => {
    const newSelectionAreaStart = extendX(start, x);
    const newSelectionAreaEnd = extendX(end, x);

    const nextSelection = clampSelectionArea(
      normalizeSelectionArea([newSelectionAreaStart, newSelectionAreaEnd]),
      range,
    );
    onSelectionChange?.({ selection: nextSelection, accessible: true });
  };
  const edgesConfig = [
    {
      id: LEFT_EDGE_ID,
      x: startX,
      onKeyDown: handleSelectionAreaStartKeyDown,
      pivot: end,
    },
    {
      id: RIGHT_EDGE_ID,
      x: endX,
      onKeyDown: handleSelectionAreaEndKeyDown,
      pivot: start,
    },
  ];
  const formatters = {
    top: topFormatter || ((_x) => undefined),
    bottom: bottomFormatter || ((_x) => undefined),
  };
  const y = height / 2 - KNOB_HEIGHT / 2;
  const pointerHanders = (id: string) => ({
    onPointerEnter: () => {
      setHoveredEdge(id);
    },
    onPointerLeave: () => {
      setHoveredEdge('');
    },
  });
  return (
    <>
      <SelectionBackdrop
        focusable={focusable}
        startX={startX}
        endX={endX}
        height={height}
        isSynchronizedSelection={false}
        interactable={interactable}
        onDragMove={onWholeSelectionMove}
        hasTopLabels={!isUndefined(topFormatter)}
        hasBottomLabels={!isUndefined(bottomFormatter)}
      />
      {edgesConfig.map((edge) => {
        return (
          <SelectionEdge
            key={edge.id}
            edgeId={edge.id}
            hoveredEdge={hoveredEdge}
            x={edge.x}
            y={y}
            focusable={focusable}
            interactable={interactable}
            onKeyDown={edge.onKeyDown}
            onDragMove={onDragMove}
            onDragStart={() => {
              selectionEdgePivotRef.current = edge.pivot;
            }}
            onDragEnd={() => (selectionEdgePivotRef.current = undefined)}
            topLabel={formatters.top(edge.x)}
            bottomLabel={formatters.bottom(edge.x)}
            chartBounds={bounds}
            isBottomLabelRotated={isBottomLabelRotated}
            isTopLabelRotated={isTopLabelRotated}
            pointerHandlers={pointerHanders(edge.id)}
          />
        );
      })}
      {/* display a 3rd knob when hovering left knob so that we can see it over the right one  */}
      {hoveredEdge === LEFT_EDGE_ID && (
        <SelectionEdge
          key={edgesConfig[0].id}
          edgeId={edgesConfig[0].id}
          hoveredEdge={hoveredEdge}
          x={edgesConfig[0].x}
          y={y}
          focusable={focusable}
          interactable={false}
          onKeyDown={edgesConfig[0].onKeyDown}
          onDragMove={onDragMove}
          onDragStart={() => {
            selectionEdgePivotRef.current = edgesConfig[0].pivot;
          }}
          onDragEnd={() => (selectionEdgePivotRef.current = undefined)}
          topLabel={formatters.top(edgesConfig[0].x)}
          bottomLabel={formatters.bottom(edgesConfig[0].x)}
          chartBounds={bounds}
          isBottomLabelRotated={isBottomLabelRotated}
          isTopLabelRotated={isTopLabelRotated}
          pointerHandlers={pointerHanders(edgesConfig[0].id)}
        />
      )}
    </>
  );
};
